import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import {
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  updateProfile
} from 'firebase/auth'
import { 
  auth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInAnonymously,
  signOut, 
  onAuthStateChanged,
  signInWithGoogle,
  getGoogleRedirectResult,
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  onSnapshot,
  db
} from '../config/firebase'
import { signInWithOkta, getOktaUser, isOktaAuthenticated, signOutOkta } from '../config/okta'
import { clearUnloadSuppression, suppressUnloadPrompt } from '../utils/unloadGuard'
import toast from 'react-hot-toast'
import { authErrorMessage, toastAuthError, toastCustomerError } from '../utils/customerError'
import { isAdminUser } from '../utils/userProfileFields'
import maintenanceConfig from '../config/maintenance.json'
import securityService from '../services/securityService'
import { fetchMyCommunityPosts, publishDesignToCommunity, unshareDesign } from '../services/designSocialService'
import { fetchMyCustomQuoteRequests } from '../services/customQuoteService'
import { fetchMyOrders } from '../services/orderCheckoutService'
import { buildDataExport } from '../utils/dataExport'
import { hostDesignImages, persistableDesignImage } from '../services/designStorage'
import {
  checkSignupAllowed,
  registerNewAccount,
  resendVerificationEmail
} from '../services/accountService'
import {
  matchDevLogin,
  buildDevSessionUser,
  isPersistedLocalSession,
  isDevTestSessionUser,
  DEV_TEST_ACCOUNTS_ENABLED
} from '../config/devTestAccounts'
import {
  DEFAULT_PREFERENCES,
  loadLocalPreferences,
  saveLocalPreferences,
  mergePreferences,
  clearLocalDrafts
} from '../utils/userPreferences'
import { pickAllowedProfileFields, cleanSignupName } from '../utils/userProfileFields'
import { isPaidSubscription } from '../data/plansConfig'
import { sessionWithoutAuth } from '../utils/authSession'
import { FAVORITES_KEY, readScoped, removeScoped } from '../utils/userScopedStorage'
import { requestPasswordReset, changeEmailPassword } from '../utils/passwordActions'

const MAINTENANCE_MODE = maintenanceConfig.MAINTENANCE_MODE === true
const MAINTENANCE_MESSAGE =
  'Style AI is temporarily down for maintenance. Please check back soon.'
const SAVE_FAILED_MESSAGE = "We couldn't save your design. It's still here, so please try again."
// Accounts the server refused at sign-up; a late auth-state callback must not restore them.
const rejectedSignupUids = new Set()
// Full Name typed at sign-up, by lowercased email, for whichever code creates the profile first.
const pendingSignupNames = new Map()

function blockAuthDuringMaintenance() {
  if (!MAINTENANCE_MODE) return
  toast.error(MAINTENANCE_MESSAGE)
  const err = new Error(MAINTENANCE_MESSAGE)
  err.code = 'MAINTENANCE'
  throw err
}

const UserContext = createContext()

function readStoredSession() {
  if (typeof window === 'undefined') {
    return { user: null, isPremium: false }
  }

  try {
    const savedUser = securityService.secureRetrieve('style_ai_user')
    const savedPremium = securityService.secureRetrieve('style_ai_premium')
    if (!savedUser) {
      return { user: null, isPremium: false }
    }
    // Purge stale dev/test sessions — production bundles must never hydrate them
    if (isDevTestSessionUser(savedUser) && !DEV_TEST_ACCOUNTS_ENABLED) {
      securityService.secureStore('style_ai_user', null)
      securityService.secureStore('style_ai_premium', 'false')
      return { user: null, isPremium: false }
    }
    return {
      user: savedUser,
      isPremium: isPaidSubscription(savedUser.subscription)
    }
  } catch (error) {
    console.error('Error reading stored session:', error)
    securityService.secureStore('style_ai_user', null)
    securityService.secureStore('style_ai_premium', 'false')
    return { user: null, isPremium: false }
  }
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

export const UserProvider = ({ children }) => {
  const initialSession = readStoredSession()
  const [user, setUser] = useState(initialSession.user)
  const [isPremium, setIsPremium] = useState(initialSession.isPremium)
  const [isLoading, setIsLoading] = useState(true)

  const persistSession = useCallback((sessionUser, premium) => {
    securityService.secureStore('style_ai_user', sessionUser)
    securityService.secureStore('style_ai_premium', premium ? 'true' : 'false')
  }, [])

  const applyLocalSessionUpdate = useCallback((updatedUser) => {
    const premium = isPaidSubscription(updatedUser.subscription)
    setUser(updatedUser)
    setIsPremium(premium)
    persistSession(updatedUser, premium)
  }, [persistSession])

  useEffect(() => {
    const buildFallbackUser = (fbUser) => ({
      uid: fbUser.uid,
      email: fbUser.email,
      name: fbUser.displayName || fbUser.email?.split('@')[0] || 'User',
      photoURL: fbUser.photoURL || null,
      provider: fbUser.providerData?.[0]?.providerId?.includes('google') ? 'google' : 'firebase',
      subscription: 'free',
      createdAt: new Date().toISOString(),
      designs: []
    })

    const applyFirestoreError = (firebaseUser, err) => {
      console.error('Firestore user document error:', err)
      toastCustomerError(err, 'Could not load your account. Please try again.', {
        id: 'firestore-permission',
        duration: 9000
      })
      const u = buildFallbackUser(firebaseUser)
      setUser(u)
      setIsPremium(false)
      securityService.secureStore('style_ai_user', u)
      securityService.secureStore('style_ai_premium', 'false')
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        // Wait until Firebase Auth has finished restoring from IndexedDB/local persistence.
        // Without this, a premature null can wipe the session right after Stripe redirects home.
        if (typeof auth.authStateReady === 'function') {
          await auth.authStateReady()
        }

        // Re-read after ready — the callback arg can be stale relative to persistence restore
        const current = auth.currentUser || firebaseUser

        if (current && rejectedSignupUids.has(current.uid)) {
          setUser(null)
          setIsPremium(false)
        } else if (current) {
          try {
            const userDoc = await getDoc(doc(db, 'users', current.uid))
            if (rejectedSignupUids.has(current.uid)) return
            if (userDoc.exists()) {
              const userData = userDoc.data()
              const fullUser = {
                uid: current.uid,
                email: current.email,
                ...userData
              }
              setUser(fullUser)
              setIsPremium(isPaidSubscription(userData.subscription))

              securityService.secureStore('style_ai_user', fullUser)
              securityService.secureStore('style_ai_premium', isPaidSubscription(userData.subscription) ? 'true' : 'false')
            } else {
              if (current.isAnonymous) {
                // Okta flow creates the doc in handleOktaCallback after signInAnonymously; skip empty row
              } else {
                const signupName =
                  pendingSignupNames.get(String(current.email || '').toLowerCase()) ||
                  cleanSignupName(current.displayName)
                const newUser = {
                  uid: current.uid,
                  email: current.email,
                  ...(signupName ? { name: signupName } : {}),
                  subscription: 'free',
                  createdAt: new Date(),
                  designs: []
                }
                await setDoc(doc(db, 'users', current.uid), newUser)
                setUser(newUser)
                setIsPremium(false)
                securityService.secureStore('style_ai_user', newUser)
                securityService.secureStore('style_ai_premium', 'false')
              }
            }
          } catch (e) {
            applyFirestoreError(current, e)
          }
        } else {
          const session = sessionWithoutAuth(
            securityService.secureRetrieve('style_ai_user'),
            securityService.secureRetrieve('style_ai_premium'),
            isPersistedLocalSession
          )
          setUser(session.user)
          setIsPremium(session.isPremium)
          if (session.clearCache) {
            securityService.secureStore('style_ai_user', null)
            securityService.secureStore('style_ai_premium', 'false')
            localStorage.removeItem('style_ai_user')
            localStorage.removeItem('style_ai_premium')
          }
        }
      } catch (err) {
        console.error('[auth] could not restore session:', err)
        setUser(null)
        setIsPremium(false)
      } finally {
        setIsLoading(false)
      }
    })

    return unsubscribe
  }, [])

  // Live plan updates from Stripe webhook (Admin SDK writes users.subscription).
  useEffect(() => {
    if (!user?.uid || isPersistedLocalSession(user) || !auth.currentUser) return undefined
    if (auth.currentUser.uid !== user.uid) return undefined
    const uid = user.uid
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (!snap.exists()) return
      const userData = snap.data()
      const paid = isPaidSubscription(userData.subscription)
      setUser((prev) => {
        if (!prev || prev.uid !== uid) return prev
        return { ...prev, ...userData, uid }
      })
      setIsPremium(paid)
      securityService.secureStore('style_ai_premium', paid ? 'true' : 'false')
    }, (err) => {
      console.warn('[auth] live profile updates stopped:', err.code || err.message)
    })
    return unsub
  }, [user?.uid])

  const discardRejectedAccount = async (uid) => {
    if (uid) rejectedSignupUids.add(uid)
    await signOut(auth).catch(() => {})
    setUser(null)
    setIsPremium(false)
    securityService.secureStore('style_ai_user', null)
    securityService.secureStore('style_ai_premium', 'false')
  }

  const saveSignupName = async (firebaseUser, name) => {
    if (!name || !firebaseUser) return
    await updateProfile(firebaseUser, { displayName: name }).catch((err) => {
      console.warn('[auth] could not set display name:', err?.code || err?.message)
    })
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), { name })
    } catch (err) {
      // not-found: the auth-state handler hasn't created the profile yet and will include the name.
      if (err?.code !== 'not-found') {
        console.warn('[auth] could not save sign-up name:', err?.code || err?.message)
      }
    }
    setUser((prev) => (prev && prev.uid === firebaseUser.uid ? { ...prev, name } : prev))
  }

  const signUp = async (email, password, fullName = '') => {
    blockAuthDuringMaintenance()
    const name = cleanSignupName(fullName)
    if (name) pendingSignupNames.set(String(email || '').trim().toLowerCase(), name)
    try {
      const devAccount = await matchDevLogin(email, password)
      if (devAccount) {
        const devUser = buildDevSessionUser(devAccount)
        setUser(devUser)
        setIsPremium(isPaidSubscription(devUser.subscription))
        securityService.secureStore('style_ai_user', devUser)
        securityService.secureStore('style_ai_premium', isPaidSubscription(devUser.subscription) ? 'true' : 'false')
        toast.success(
          devUser.role === 'admin'
            ? 'Admin account created! Pro access is on.'
            : `${devAccount.label} account ready for testing.`
        )
        return devUser
      }

      await checkSignupAllowed(email)
      // Firebase auth — onAuthStateChanged loads/creates the Firestore profile
      const credential = await createUserWithEmailAndPassword(auth, email, password)
      try {
        await registerNewAccount(credential.user)
      } catch (err) {
        if (err.accountRejected) await discardRejectedAccount(credential.user.uid)
        throw err
      }
      await saveSignupName(credential.user, name)
      try {
        await resendVerificationEmail(credential.user)
        toast.success('Account created! Check your email for a link to verify it.')
      } catch (verifyErr) {
        console.warn('[auth] verification email failed:', verifyErr?.code || verifyErr?.message)
        toast.success('Account created! Please verify your email to start designing.')
      }
      return auth.currentUser
    } catch (error) {
      console.error('Sign up error:', error)
      if (error.accountRejected) toastCustomerError(error, "We couldn't create your account. Please try again.")
      else toastAuthError(error)
      throw error
    }
  }

  const signIn = async (email, password) => {
    blockAuthDuringMaintenance()
    try {
      const devAccount = await matchDevLogin(email, password)
      if (devAccount) {
        const devUser = buildDevSessionUser(devAccount)
        setUser(devUser)
        setIsPremium(isPaidSubscription(devUser.subscription))
        securityService.secureStore('style_ai_user', devUser)
        securityService.secureStore('style_ai_premium', isPaidSubscription(devUser.subscription) ? 'true' : 'false')
        toast.success(
          devUser.role === 'admin'
            ? 'Welcome Admin! Pro access is on.'
            : `Signed in as ${devAccount.label}.`
        )
        return devUser
      }

      await signInWithEmailAndPassword(auth, email, password)
      toast.success('Welcome back!')
      return auth.currentUser
    } catch (error) {
      console.error('Sign in error:', error)
      toastAuthError(error)
      throw error
    }
  }

  const sendPasswordReset = async (email) => {
    blockAuthDuringMaintenance()
    return requestPasswordReset({
      email,
      send: (address) => sendPasswordResetEmail(auth, address),
      describeError: authErrorMessage
    })
  }

  const changePassword = (currentPassword, newPassword) =>
    changeEmailPassword({
      firebaseUser: auth.currentUser,
      currentPassword,
      newPassword,
      credentialFor: (email, password) => EmailAuthProvider.credential(email, password),
      reauthenticate: reauthenticateWithCredential,
      update: updatePassword,
      describeError: authErrorMessage
    })

  // Google OAuth sign-in
  const signInWithGoogleAuth = async () => {
    blockAuthDuringMaintenance()
    try {
      // Check if Firebase is properly configured (not using demo values)
      const apiKey = import.meta.env.VITE_FIREBASE_API_KEY
      if (!apiKey || apiKey === 'demo-api-key') {
        console.error('Sign-in is not configured')
        toast.error(authErrorMessage({ code: 'unknown' }))
        return
      }

      // Use Firebase Google authentication (first-time Google user = new account, same as Sign up)
      const result = await signInWithGoogle()
      const firebaseUser = result.user
      const googleProfile = getAdditionalUserInfo(result)
      const isNewGoogleUser = googleProfile?.isNewUser === true
      if (isNewGoogleUser) {
        try {
          await registerNewAccount(firebaseUser)
        } catch (err) {
          if (err.accountRejected) await discardRejectedAccount(firebaseUser.uid)
          throw err
        }
      }

      // Create user data from Google profile
      const userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Google User',
        photoURL: firebaseUser.photoURL || null,
        provider: 'google',
        subscription: 'free',
        createdAt: new Date().toISOString(),
        designs: []
      }
      
      let firestoreOk = true
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
        if (userDoc.exists()) {
          const existingData = userDoc.data()
          const fullUser = { ...userData, ...existingData }
          setUser(fullUser)
          setIsPremium(isPaidSubscription(existingData.subscription))
          securityService.secureStore('style_ai_user', fullUser)
          securityService.secureStore('style_ai_premium', isPaidSubscription(existingData.subscription) ? 'true' : 'false')
        } else {
          await setDoc(doc(db, 'users', firebaseUser.uid), userData)
          setUser(userData)
          setIsPremium(false)
          securityService.secureStore('style_ai_user', userData)
          securityService.secureStore('style_ai_premium', 'false')
        }
      } catch (fsErr) {
        firestoreOk = false
        console.error('Google sign-in Firestore error:', fsErr)
        setUser(userData)
        setIsPremium(false)
        securityService.secureStore('style_ai_user', userData)
        securityService.secureStore('style_ai_premium', 'false')
        toastCustomerError(fsErr, 'Could not save your account. Please try again.', {
          id: 'firestore-permission',
          duration: 9000
        })
      }

      if (firestoreOk) {
        toast.success(
          isNewGoogleUser ? 'Welcome! Your account was created with Google.' : 'Signed in with Google!'
        )
      }
      return userData
    } catch (error) {
      console.error('Google sign-in error:', error)
      if (error.accountRejected) toastCustomerError(error, "We couldn't create your account. Please try again.")
      else toastAuthError(error)
      throw error
    }
  }

  // Okta SSO sign-in
  const signInWithOktaAuth = async () => {
    blockAuthDuringMaintenance()
    try {
      // Check if Okta is properly configured (not using demo values)
      const oktaIssuer = import.meta.env.VITE_OKTA_ISSUER
      if (!oktaIssuer || oktaIssuer.includes('dev-123456') || oktaIssuer.includes('YOUR-OKTA-DOMAIN')) {
        console.error('Okta configuration missing')
        toast.error(authErrorMessage({ code: 'unknown' }))
        throw new Error('Sign-in is not configured')
      }

      // Redirect to Okta for authentication
      await signInWithOkta()
      // User will be redirected to Okta, then back to handleOktaCallback
    } catch (error) {
      console.error('Okta sign-in error:', error)
      toastAuthError(error)
      throw error
    }
  }

  // Handle Okta callback (call after oktaAuth.handleRedirect() in OktaCallback)
  const handleOktaCallback = useCallback(async () => {
    const isAuthenticated = await isOktaAuthenticated()
    if (!isAuthenticated) {
      throw new Error('Okta sign-in did not complete')
    }

    const oktaUser = await getOktaUser()

    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      throw new Error('Sign out of your current account before using Okta SSO.')
    }

    if (!auth.currentUser) {
      await signInAnonymously(auth)
    }

    const fbUid = auth.currentUser.uid
    const profileRef = doc(db, 'oktaProfiles', oktaUser.sub)
    const userRef = doc(db, 'users', fbUid)
    const [profileSnap, userSnap] = await Promise.all([getDoc(profileRef), getDoc(userRef)])
    const savedProfile = profileSnap.exists() ? profileSnap.data() : null
    const existingUser = userSnap.exists() ? userSnap.data() : null

    const userData = {
      uid: fbUid,
      oktaSub: oktaUser.sub,
      email: oktaUser.email || savedProfile?.email,
      name: oktaUser.name || savedProfile?.name || oktaUser.email?.split('@')[0] || 'User',
      photoURL: oktaUser.picture || savedProfile?.photoURL || null,
      provider: 'okta',
      subscription: existingUser?.subscription || 'free',
      createdAt: existingUser?.createdAt || savedProfile?.createdAt || new Date().toISOString(),
      designs: existingUser?.designs || savedProfile?.designs || []
    }

    const oktaProfilePayload = pickAllowedProfileFields({
      uid: fbUid,
      oktaSub: oktaUser.sub,
      email: userData.email,
      name: userData.name,
      photoURL: userData.photoURL,
      provider: 'okta',
      createdAt: userData.createdAt,
      designs: userData.designs
    })

    const sharedFields = {
      uid: fbUid,
      oktaSub: oktaUser.sub,
      email: userData.email,
      name: userData.name,
      photoURL: userData.photoURL,
      provider: 'okta'
    }

    try {
      if (existingUser) {
        await setDoc(
          userRef,
          {
            ...sharedFields,
            designs: userData.designs
          },
          { merge: true }
        )
      } else {
        await setDoc(userRef, {
          ...sharedFields,
          subscription: 'free',
          createdAt: userData.createdAt,
          designs: userData.designs
        })
      }
      await setDoc(
        profileRef,
        { ...oktaProfilePayload, lastFirebaseUid: fbUid, updatedAt: new Date().toISOString() },
        { merge: true }
      )
    } catch (e) {
      console.error('Okta Firestore error:', e)
      toastCustomerError(e, 'Could not save your account. Please try again.', {
        id: 'firestore-permission',
        duration: 8000
      })
      throw e
    }

    setUser(userData)
    setIsPremium(isPaidSubscription(userData.subscription))
    securityService.secureStore('style_ai_user', userData)
    securityService.secureStore('style_ai_premium', isPaidSubscription(userData.subscription) ? 'true' : 'false')
    toast.success('Signed in with Okta!')
    return userData
  }, [])

  const logout = async () => {
    const redirectsAway = user?.provider === 'okta'
    if (redirectsAway) suppressUnloadPrompt()
    try {
      if (redirectsAway) {
        await signOutOkta()
        if (auth.currentUser) {
          await signOut(auth)
        }
      } else {
        await signOut(auth)
      }
      
      setUser(null)
      setIsPremium(false)
      
      // Clear secure storage
      securityService.secureStore('style_ai_user', null)
      securityService.secureStore('style_ai_premium', 'false')
      
      toast.success('Signed out successfully')
    } catch (error) {
      console.error('Logout error:', error)
      if (redirectsAway) clearUnloadSuppression()
      // Clear local state even if logout fails
      setUser(null)
      setIsPremium(false)
      securityService.secureStore('style_ai_user', null)
      securityService.secureStore('style_ai_premium', 'false')
      toast.error('Error signing out')
    }
  }

  const upgradeToPremium = async (_billingData = null) => {
    if (!user) {
      toast.error('Please sign in to upgrade')
      return
    }

    // Local dev test sessions only — never persisted to Firestore in production builds.
    if (isPersistedLocalSession(user)) {
      const updatedUser = {
        ...user,
        subscription: 'premium',
        upgradedAt: new Date().toISOString()
      }
      applyLocalSessionUpdate(updatedUser)
      toast.success('Pro is on for this account.')
      return
    }

    // Real accounts: tier changes only via server (Stripe webhook → Admin SDK).
    toast.error('To upgrade to Pro, open Settings and choose Billing.')
    throw new Error('Subscription changes require verified payment')
  }

  const saveDesign = async (design) => {
    if (!user) {
      toast.error('Please sign in to save designs')
      return
    }

    try {
      let designToSave = { ...design }

      if (!isPersistedLocalSession(user)) {
        designToSave = await hostDesignImages(designToSave, user.uid)
        const imageSource = designToSave.mainImage || designToSave.image
        if (imageSource) {
          const permanentUrl = await persistableDesignImage(imageSource, user.uid)
          designToSave = {
            ...designToSave,
            mainImage: permanentUrl,
            ...(designToSave.image ? { image: permanentUrl } : {})
          }
        }
      }

      const newDesign = {
        id: Date.now().toString(),
        ...designToSave,
        createdAt: new Date()
      }

      if (isPersistedLocalSession(user)) {
        applyLocalSessionUpdate({
          ...user,
          designs: [...(user.designs || []), newDesign]
        })
        toast.success('Design saved successfully!')
        return newDesign
      }

      const userRef = doc(db, 'users', user.uid)
      const userDoc = await getDoc(userRef)
      const designs = userDoc.data()?.designs || []

      await updateDoc(userRef, {
        designs: [...designs, newDesign]
      })

      setUser((prev) => ({
        ...prev,
        designs: [...(prev.designs || []), newDesign]
      }))
      
      toast.success('Design saved successfully!')
      return newDesign
    } catch (error) {
      console.error('Error saving design:', error)
      toastCustomerError(error, SAVE_FAILED_MESSAGE, {
        isAdmin: isAdminUser(user)
      })
      error.customerNotified = true
      throw error
    }
  }

  const updateUserProfile = async (profileData) => {
    if (!user) {
      toast.error('Please sign in to update your profile')
      throw new Error('Not signed in')
    }

    const payload = {
      ...pickAllowedProfileFields(profileData),
      profileUpdatedAt: new Date().toISOString()
    }

    const updatedUser = { ...user, ...payload }

    if (isPersistedLocalSession(user)) {
      applyLocalSessionUpdate(updatedUser)
      return updatedUser
    }

    await updateDoc(doc(db, 'users', user.uid), payload)
    setUser(updatedUser)
    persistSession(updatedUser, isPremium)
    return updatedUser
  }

  const getUserPreferences = useCallback(() => {
    if (user?.preferences) {
      return mergePreferences(user.preferences)
    }
    return loadLocalPreferences(user?.uid)
  }, [user])

  const updateUserPreferences = async (partial) => {
    const current = getUserPreferences()
    const next = mergePreferences({ ...current, ...partial })

    if (!user) {
      saveLocalPreferences(next, null)
      return next
    }

    const updatedUser = { ...user, preferences: next }

    if (isPersistedLocalSession(user)) {
      applyLocalSessionUpdate(updatedUser)
      saveLocalPreferences(next, user.uid)
      return next
    }

    await updateDoc(doc(db, 'users', user.uid), {
      preferences: next,
      preferencesUpdatedAt: new Date().toISOString()
    })
    setUser(updatedUser)
    persistSession(updatedUser, isPremium)
    saveLocalPreferences(next, user.uid)
    return next
  }

  // Each flag is opt-in so clearing favorites or preferences never touches an in-progress draft.
  const clearUserLocalData = async (options = {}) => {
    const { drafts = false, favorites = false, preferences = false } = options

    if (drafts) clearLocalDrafts(user?.uid)
    if (favorites) removeScoped(FAVORITES_KEY, user?.uid)
    if (preferences) {
      saveLocalPreferences(DEFAULT_PREFERENCES, user?.uid)
      if (user) {
        await updateUserPreferences(DEFAULT_PREFERENCES)
      }
    }
  }

  const exportUserData = async () => {
    const unavailable = []
    const load = async (label, fetcher) => {
      try {
        return await fetcher()
      } catch {
        unavailable.push(label)
        return []
      }
    }
    const signedIn = Boolean(user?.uid && auth.currentUser)
    const [orders, customQuotes, communityPosts] = await Promise.all([
      signedIn ? load('orders', fetchMyOrders) : [],
      user?.uid ? load('customQuotes', () => fetchMyCustomQuoteRequests(user.uid, user)) : [],
      signedIn ? load('communityPosts', () => fetchMyCommunityPosts(user.uid)) : []
    ])
    const exportPayload = buildDataExport({
      user,
      preferences: getUserPreferences(),
      favorites: readScoped(FAVORITES_KEY, user?.uid) || [],
      orders,
      customQuotes,
      communityPosts,
      unavailable
    })

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `style-ai-data-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
    return exportPayload
  }

  const deleteDesign = async (designId) => {
    if (!user) {
      toast.error('Please sign in to manage designs')
      throw new Error('Not signed in')
    }

    if (isPersistedLocalSession(user)) {
      const next = (user.designs || []).filter((d) => d.id !== designId)
      applyLocalSessionUpdate({ ...user, designs: next })
      toast.success('Design removed')
      return
    }

    try {
      await unshareDesign(designId, user.uid)
    } catch (error) {
      toastCustomerError(error, "We couldn't remove this design from Explore, so it wasn't deleted. Please try again.", {
        isAdmin: isAdminUser(user)
      })
      error.customerNotified = true
      throw error
    }

    const nextDesigns = (user.designs || []).filter((d) => d.id !== designId)
    await updateDoc(doc(db, 'users', user.uid), { designs: nextDesigns })
    setUser((prev) => ({ ...prev, designs: nextDesigns }))
    toast.success('Design deleted')
  }

  const unshareDesignPost = async (designId) => {
    if (!user) {
      toast.error('Please sign in to manage designs')
      throw new Error('Not signed in')
    }
    try {
      await unshareDesign(designId, user.uid)
      toast.success('Removed from Explore')
    } catch (error) {
      toastCustomerError(error, "We couldn't remove this design from Explore. Please try again.", {
        isAdmin: isAdminUser(user)
      })
      throw error
    }
  }

  const updateDesign = async (designId, updates) => {
    if (!user) {
      toast.error('Please sign in to manage designs')
      throw new Error('Not signed in')
    }

    const existing = (user.designs || []).find((d) => d.id === designId)
    if (!existing) {
      throw new Error('Design not found')
    }

    const hostedUpdates = isPersistedLocalSession(user)
      ? updates
      : await hostDesignImages(updates, user.uid)
    const nextDesigns = (user.designs || []).map((d) =>
      d.id === designId ? { ...d, ...hostedUpdates, updatedAt: new Date().toISOString() } : d
    )

    if (isPersistedLocalSession(user)) {
      applyLocalSessionUpdate({ ...user, designs: nextDesigns })
      return nextDesigns.find((d) => d.id === designId)
    }

    await updateDoc(doc(db, 'users', user.uid), { designs: nextDesigns })
    setUser((prev) => ({ ...prev, designs: nextDesigns }))
    return nextDesigns.find((d) => d.id === designId)
  }

  const shareDesign = async (design) => {
    if (!user) {
      toast.error('Please sign in to share designs')
      throw new Error('Not signed in')
    }
    try {
      const published = await publishDesignToCommunity(
        { ...design, specifications: design.specifications || design },
        user
      )
      toast.success('Your design is public on Explore')
      return published
    } catch (error) {
      toastCustomerError(error, "We couldn't share your design. Please try again.", {
        isAdmin: isAdminUser(user)
      })
      throw error
    }
  }

  const value = {
    user,
    isPremium,
    isLoading,
    signUp,
    signIn,
    signInWithGoogleAuth,
    sendPasswordReset,
    changePassword,
    signInWithOktaAuth,
    handleOktaCallback,
    logout,
    upgradeToPremium,
    saveDesign,
    deleteDesign,
    updateDesign,
    shareDesign,
    updateUserProfile,
    getUserPreferences,
    updateUserPreferences,
    clearUserLocalData,
    exportUserData,
    unshareDesign: unshareDesignPost
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
} 